import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import { Dashboard } from "./components/Dashboard";
import { Login } from "./components/Login";
import { MarketingLanding } from "./components/MarketingLanding";
import { FAQ } from "./components/FAQ";
import {
  VsNightbot,
  VsStreamElements,
  VsAilicia,
} from "./components/ComparisonPage";
import { BestTwitchAIChatbot } from "./components/BestTwitchAIChatbot";
import Footer from "./components/Footer";
import TimezoneSetup from "./components/TimezoneSetup";
import BotAuthCallback from "./components/BotAuthCallback";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import { LoadingProvider } from "./contexts/LoadingContext";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { clearSession, getImpersonation } from "./auth/session";

// Umami analytics is loaded from public/index.html. Once a user authenticates,
// tag their session so events + replays are attributable in the Umami dashboard.
// `defer` means the script may not be ready when this runs — bail quietly if so;
// pageview tracking continues anonymously and is correct.
declare global {
  interface Window {
    umami?: {
      identify(data: Record<string, unknown>): void;
    };
  }
}

interface AuthenticatedUser {
  id?: string;
  username?: string;
  twitchId?: string;
  userType?: string;
  email?: string;
}

function identifyToUmami(user: AuthenticatedUser): void {
  if (!user?.id) return;
  try {
    window.umami?.identify({
      id: user.id,
      username: user.username,
      twitchId: user.twitchId,
      userType: user.userType,
    });
  } catch {
    // Umami is a fire-and-forget side channel — never let it break the UI.
  }
}

function AppContent() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const impersonation = getImpersonation();

  // Scroll to top on route change (SPA <Link> otherwise keeps the old scroll
  // position — e.g. clicking a comparison link at the bottom of /vs/nightbot
  // would land you at the bottom of the next page). Skip the dashboard so
  // in-app navigation keeps its own scroll behavior.
  useEffect(() => {
    if (!location.pathname.startsWith("/dashboard")) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // Check if we're on the login page
  const isLoginPage = location.pathname === "/login";
  // Public-facing surfaces all use pure-black background to match the
  // marketing aesthetic; dashboard keeps its slightly-tinted background.
  const isPublicPage = ["/", "/login", "/faq", "/privacy", "/terms"].includes(
    location.pathname,
  );

  useEffect(() => {
    // Check for token in URL params (returned from OAuth callback)
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (token) {
      // A fresh login always replaces any parked impersonation session.
      clearSession();
      // Store token and redirect to clean URL
      localStorage.setItem("access_token", token);
      window.history.replaceState({}, document.title, "/dashboard");
      validateToken(token);
    } else {
      // Check for existing token
      const storedToken = localStorage.getItem("access_token");
      if (storedToken) {
        validateToken(storedToken);
      } else {
        setLoading(false);
      }
    }
  }, []);

  const validateToken = async (token: string) => {
    try {
      const response = await fetch(`/api/auth/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        identifyToUmami(userData);
      } else {
        throw new Error("Token validation failed");
      }
    } catch (error) {
      console.error("Token validation failed:", error);
      localStorage.removeItem("access_token");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    window.location.href = `/api/auth/twitch`;
  };

  // Remove loading screen to prevent flash
  if (loading && !user) {
    // Only show nothing when truly loading initial auth check
    return null;
  }

  return (
    <div
      className="App"
      style={{
        minHeight: "100vh",
        backgroundColor: isPublicPage ? "#0a0a0a" : "#0f1419",
      }}
    >
      {/* Main Content */}
      <main
        style={{
          paddingTop: "0", // Dashboard handles its own padding
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Routes>
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to="/dashboard/control" replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/dashboard/*"
            element={
              user ? (
                <div style={{ width: "100%" }}>
                  <Dashboard user={user} />
                </div>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="/auth/timezone-setup" element={<TimezoneSetup />} />
          <Route path="/auth/bot-callback" element={<BotAuthCallback />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/vs/nightbot" element={<VsNightbot />} />
          <Route path="/vs/streamelements" element={<VsStreamElements />} />
          <Route path="/vs/ai-licia" element={<VsAilicia />} />
          <Route
            path="/best-twitch-ai-chatbot"
            element={<BestTwitchAIChatbot />}
          />
          <Route
            path="/support"
            element={<Navigate to="/dashboard/support" replace />}
          />
          <Route
            path="/"
            element={
              user ? (
                <Navigate to="/dashboard/control" replace />
              ) : (
                <MarketingLanding onLogin={handleLogin} />
              )
            }
          />
        </Routes>
      </main>

      {/* Footer - Hidden on login page */}
      {!isLoginPage && <Footer />}

      {user && impersonation && <ImpersonationBanner info={impersonation} />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <LoadingProvider>
          <AppContent />
        </LoadingProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
